/**
 * 요건·근거 링크 규칙 (2026-09-16, 61차 — Ciel HS 상세의 "명사마다 근거 화면" 구조를 벤치마킹).
 *
 * 화면에 보이는 명사를 결정론 규칙으로 그 근거 화면에 잇는다. LLM·정규식 추측 없음, 문구는 법령 원문·우리 자료만.
 *  - 법령명            → 우리 `법령별 통관정보`(AI 가이드·소관부처·전화)로 이동 + `↗` law.go.kr 원문
 *  - 64차(2026-09-16): 화면 사이 링크는 **진짜 `<a href>`** — Ctrl·Shift·휠 클릭·우클릭은 언제나 브라우저가 새 탭.
 *    65차: 평범한 클릭은 **설정**(내 계정 설정 › 연계 화면 열기)대로 — 기본 새 탭(`target=_blank`), 같은 탭을 고르면 SPA 이동.
 *    사용자 결정(2026-09-16): "탭이 더 나아 보이니 기본을 탭으로, 혹시 모르니 설정에 옵션". 주소는 지금 화면 파라미터를 끌고 가지 않는다.
 *    "돌아왔을 때 보던 자리"는 QuickRefPage·HeadingDetailPage 가 남의 화면 URL 의 popstate 를 무시하고,
 *    App 이 속견표 패널을 visibility 로 숨겨(스크롤 보존) 해결한다.
 *  - 법령명 제N조      → law.go.kr 조문 한글주소(`/법령/<법령명>/제N조`, 띄어쓰기 유무 모두 열림 — 2026-09-16 실측)
 *  - 별표 N            → 그 법령의 law.go.kr 화면(별표 한글주소는 없다 — 실측 오류페이지) · 별표 탭 안내
 *  - 소관부처(기관명)  → 우리 `요건확인기관 연락처`에 부처 필터
 *  - 전화              → tel:
 *
 * 요건 본문의 `[법령명]` 괄호와 항목의 `agencies[].law`가 이미 구조화돼 있어 그 이름만 잇는다(본문 안 자유 문장은 손대지 않음).
 */
import React, { useEffect, useState } from "react";

export const normalizeLawName = (s) => String(s || "").replace(/[\s·「」()（）]/g, "");

export const lawGovUrl = (law) => `https://www.law.go.kr/법령/${encodeURIComponent(String(law || "").trim())}`;
export const lawArticleUrl = (law, article) =>
  article ? `${lawGovUrl(law)}/${encodeURIComponent(`제${article}조`)}` : lawGovUrl(law);

const LAW_SUFFIX = /(법|법률|규칙|시행령|령|고시|공고|규정|훈령|예규)$/;
/** 괄호 안 글자가 법령명으로 보이는가 — 아는 법령과 같거나 법령 접미사로 끝난다. */
export function looksLikeLaw(name, knownLaws = []) {
  const n = normalizeLawName(name);
  if (!n || n.length < 3 || n.length > 60) return false;
  if (knownLaws.some((k) => normalizeLawName(k) === n)) return true;
  return LAW_SUFFIX.test(n);
}

/** 딥링크 주소 — 지금 화면의 파라미터(hs·hsk·sq·cat…)를 끌고 가지 않는다. */
export function appUrl(params) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== "") p.set(k, String(v)); });
  return "/?" + p.toString();
}
export const lawGuideUrl = (law) => appUrl({ audit: "req-guide", cat: "req_by_law", law: String(law || "").trim() });
export const agencyDirectoryUrl = (ministry) => appUrl({ audit: "tools", tool: "req-agencies", ministry: String(ministry || "").trim() });
export const reqGuideUrl = (cat, hsk) => appUrl({ audit: "req-guide", cat, hsk: hsk ? String(hsk).replace(/\D/g, "") : "" });
export function hskDetailUrl(hsk) {
  const d = String(hsk || "").replace(/\D/g, "");
  return appUrl({ audit: "onto-detail", hs: d.slice(0, 4), hsk: d.length >= 10 ? d : "" });
}

/** 같은 탭 SPA 이동(pushState + popstate). 브라우저 뒤로 가기가 그대로 동작한다. */
export function navigateSameTab(url) {
  window.history.pushState({}, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// ── 65차: 연계 화면 열기 설정 — 새 탭(기본) | 같은 탭. 브라우저 localStorage, 내 계정 설정에서 바꾼다. ──
export const LINK_TARGET_KEY = "loginhs:link-target";
export const LINK_TARGET_EVENT = "loginhs:link-target";
export const LINK_TARGET_DEFAULT = "tab";
export const LINK_TARGETS = [
  { id: "tab", label: "새 탭으로 열기", desc: "보던 세번 상세가 그대로 남고, 참조 화면은 옆 탭에 열립니다. 탭을 닫으면 원래 화면입니다." },
  { id: "same", label: "같은 탭에서 이동", desc: "한 탭 안에서 오가며, 브라우저 뒤로 가기로 보던 자리(세번·스크롤·패널)로 돌아옵니다." },
];

export function getLinkTarget() {
  try {
    const v = window.localStorage.getItem(LINK_TARGET_KEY);
    return LINK_TARGETS.some((t) => t.id === v) ? v : LINK_TARGET_DEFAULT;
  } catch (_) { return LINK_TARGET_DEFAULT; }
}

export function setLinkTarget(id) {
  const v = LINK_TARGETS.some((t) => t.id === id) ? id : LINK_TARGET_DEFAULT;
  try { window.localStorage.setItem(LINK_TARGET_KEY, v); } catch (_) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(LINK_TARGET_EVENT, { detail: v })); } catch (_) { /* ignore */ }
  return v;
}

/** 설정을 구독한다(설정 화면에서 바꾸면 열린 링크들의 target 이 같이 바뀐다). */
export function useLinkTarget() {
  const [t, setT] = useState(getLinkTarget);
  useEffect(() => {
    const on = () => setT(getLinkTarget());
    window.addEventListener(LINK_TARGET_EVENT, on);
    window.addEventListener("storage", on);
    return () => { window.removeEventListener(LINK_TARGET_EVENT, on); window.removeEventListener("storage", on); };
  }, []);
  return t;
}

/** 새 탭으로 연다(팝업이 막히면 같은 탭 SPA 이동). */
export function openNewTab(url) {
  let w = null;
  try { w = window.open(url, "_blank", "noopener"); } catch (_) { w = null; }
  if (!w) navigateSameTab(url);
  return w;
}

/** 설정에 따라 연다 — 버튼·라벨 같은 비앵커 요소에서 쓴다. */
export function openLink(url) {
  if (getLinkTarget() === "tab") return openNewTab(url);
  return navigateSameTab(url);
}

/** 보조키 없는 왼쪽 클릭만 SPA 가 가로챈다 — Ctrl/Cmd/Shift/휠 클릭은 브라우저 기본(새 탭·새 창)에 맡긴다. */
export function isPlainClick(e) {
  if (!e) return true;
  const leftButton = e.button === 0 || e.button === undefined;
  return leftButton && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * 앵커 onClick — 평범한 클릭이면 설정대로(새 탭이면 앵커의 target=_blank 에 맡기고 가로채지 않음, 같은 탭이면 SPA 이동),
 * 보조키·휠 클릭은 항상 브라우저에 맡김.
 */
export function spaClick(url) {
  return (e) => {
    if (!isPlainClick(e)) return;
    if (getLinkTarget() === "tab") return; // <a target="_blank"> 기본 동작
    e.preventDefault();
    navigateSameTab(url);
  };
}

/** 앵커 target/rel 속성 — 설정이 새 탭이면 _blank. */
export function anchorTargetProps(target) {
  return target === "tab" ? { target: "_blank", rel: "noopener" } : {};
}

/** 우리 `법령별 통관정보`에서 그 법령을 연다(설정대로). */
export function goLawGuide(law) { return openLink(lawGuideUrl(law)); }

/** 우리 `요건확인기관 연락처`를 그 부처로 걸러 연다(설정대로). */
export function goAgencyDirectory(ministry) { return openLink(agencyDirectoryUrl(ministry)); }

const linkBase = {
  color: "#1d4ed8",
  textDecoration: "underline dotted",
  textUnderlineOffset: 2,
  cursor: "pointer",
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
};
const extStyle = { color: "#1d4ed8", textDecoration: "none", fontSize: "0.85em", marginLeft: 2 };

/**
 * 법령명 링크. article 이 있으면 law.go.kr 조문으로 바로(새 탭), 없으면 우리 법령별 통관정보 + `↗` 원문.
 * label 은 화면에 보일 글자(원문 표기 그대로), law 는 이어 갈 법령명(agencies 표기 우선).
 */
export function LawLink({ law, label, article, muted }) {
  const text = label || law;
  const target = useLinkTarget();
  if (article) {
    return (
      <a
        href={lawArticleUrl(law, article)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="req-law-article"
        title={`법령정보센터 · ${law} 제${article}조`}
        style={{ ...linkBase, color: muted ? "var(--audit-text-muted)" : linkBase.color }}
      >
        {text}
      </a>
    );
  }
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <a
        href={lawGuideUrl(law)}
        onClick={spaClick(lawGuideUrl(law))}
        {...anchorTargetProps(target)}
        data-testid="req-law-link"
        title={`법령별 통관정보 · ${law}${target === "tab" ? " (새 탭)" : ""}`}
        style={{ ...linkBase, color: muted ? "var(--audit-text-muted)" : linkBase.color, whiteSpace: "normal" }}
      >
        {text}
      </a>
      <a
        href={lawGovUrl(law)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="req-law-ext"
        title="법령정보센터 원문"
        style={extStyle}
        aria-label={`${law} 원문`}
      >
        ↗
      </a>
    </span>
  );
}

/** 별표 N — 그 법령의 law.go.kr 화면(별표·서식 탭). */
export function AnnexLink({ law, no, label }) {
  if (!law) return <>{label || `별표${no}`}</>;
  return (
    <a
      href={lawGovUrl(law)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="req-annex-link"
      title={`법령정보센터 · ${law} 별표${no} (법령 화면의 별표·서식 탭)`}
      style={linkBase}
    >
      {label || `별표${no}`}
    </a>
  );
}

/** 소관부처 → 요건확인기관 연락처(부처 필터). */
export function AgencyLink({ ministry, label, style }) {
  const target = useLinkTarget();
  if (!ministry) return null;
  return (
    <a
      href={agencyDirectoryUrl(ministry)}
      onClick={spaClick(agencyDirectoryUrl(ministry))}
      {...anchorTargetProps(target)}
      data-testid="req-agency-link"
      title={`요건확인기관 연락처 · ${ministry}${target === "tab" ? " (새 탭)" : ""}`}
      style={{ ...linkBase, ...style }}
    >
      {label || ministry}
    </a>
  );
}

export function TelLink({ tel }) {
  if (!tel) return null;
  const first = String(tel).split(/[,，]/)[0].replace(/[^0-9]/g, "");
  return (
    <a href={`tel:${first}`} data-testid="req-tel-link" style={{ color: "inherit", textDecoration: "none", fontVariantNumeric: "tabular-nums" }}>
      {tel}
    </a>
  );
}

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * 요건 본문 한 줄을 링크 조각으로 나눈다.
 *  - `[법령명]`           → 괄호는 그대로, 안의 법령명만 링크
 *  - `<아는 법령명> 제N조` → 조문 링크(law.go.kr), 법령명 표기는 원문(띄어쓰기 유무) 그대로
 *  - `<아는 법령명>`       → 법령 링크
 *  - `별표 N`              → 같은 줄에서 마지막으로 나온 법령(없으면 항목의 첫 법령) 화면
 * knownLaws 는 항목의 agencies[].law. 아는 법령이 없어도 괄호 법령은 접미사 규칙으로 잇는다.
 */
export function linkifyReqLine(line, { laws = [] } = {}) {
  const text = String(line || "");
  if (!text) return [];
  const known = laws.filter(Boolean);
  const byNorm = new Map(known.map((k) => [normalizeLawName(k), k]));
  const nameAlts = known
    .flatMap((k) => [k.trim(), normalizeLawName(k)])
    .filter((s) => s.length >= 3)
    .sort((a, b) => b.length - a.length)
    .map(esc);
  const parts = [`\\[([^\\[\\]\\n]{3,60})\\]`, `(별표\\s?(\\d{1,3}))`];
  if (nameAlts.length) parts.push(`((?:${nameAlts.join("|")})(?:\\s*제\\s?(\\d{1,4})조)?)`);
  const re = new RegExp(parts.join("|"), "g");
  // 괄호 법령은 그 문장 "뒤"에 붙는다(관세청 요건 표기: `… 별표9의 … 승인을 받아 수입할 수 있음 [생물다양성 …]`).
  // 별표 N은 같은 줄에서 뒤에 오는 첫 괄호 법령 → 없으면 앞에서 마지막 법령 → 없으면 항목 첫 법령.
  const bracketLaws = [];
  for (const bm of text.matchAll(/\[([^[\]\n]{3,60})\]/g)) {
    const inner = bm[1];
    const law = byNorm.get(normalizeLawName(inner)) || (looksLikeLaw(inner, known) ? inner.trim() : null);
    if (law) bracketLaws.push({ at: bm.index, law });
  }
  const annexLaw = (at, before) => (bracketLaws.find((b) => b.at > at) || {}).law || before || known[0] || null;
  const out = [];
  let last = 0; let lastLaw = null; let m; let key = 0;
  const push = (node) => out.push(React.isValidElement(node) ? React.cloneElement(node, { key: key++ }) : node);
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) push(text.slice(last, m.index));
    if (m[1] != null) {
      const inner = m[1];
      const law = byNorm.get(normalizeLawName(inner));
      if (law || looksLikeLaw(inner, known)) {
        lastLaw = law || inner.trim();
        push("["); push(<LawLink law={lastLaw} label={inner} />); push("]");
      } else push(m[0]);
    } else if (m[2] != null) {
      push(<AnnexLink law={annexLaw(m.index, lastLaw)} no={m[3]} label={m[2]} />);
    } else if (m[4] != null) {
      const article = m[5] || null;
      const shown = article ? m[4].slice(0, m[4].search(/\s*제\s?\d{1,4}조$/)) : m[4];
      const law = byNorm.get(normalizeLawName(shown)) || shown;
      lastLaw = law;
      push(<LawLink law={law} label={m[4]} article={article} />);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) push(text.slice(last));
  return out;
}
